import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import FirstPage from '@mui/icons-material/FirstPage';
import Fullscreen from '@mui/icons-material/Fullscreen';
import FullscreenExit from '@mui/icons-material/FullscreenExit';
import GetApp from '@mui/icons-material/GetApp';
import LastPage from '@mui/icons-material/LastPage';
import NavigateBefore from '@mui/icons-material/NavigateBefore';
import NavigateNext from '@mui/icons-material/NavigateNext';
import ZoomIn from '@mui/icons-material/ZoomIn';
import ZoomOut from '@mui/icons-material/ZoomOut';
import classNames from 'classnames';
import { canBeFullScreen } from '../helper';
import { DocumentActionsProps } from './DocumentActionsProps';
import { useFullScreenStyles } from '../useFullScreenStyles';
import ThemableIconButton from '../../../theme/components/Button/ThemableIconButton';
import useAppMode from '../../../lib/hook/config/useAppMode';
import useDarkTheme from '../../../theme/styles/useDarkTheme';

const DocumentActions: React.FC<DocumentActionsProps> = (
  props: DocumentActionsProps
): JSX.Element => {
  const fullScreenClasses = useFullScreenStyles();
  const darkThemeClasses = useDarkTheme();
  const { isEmbeddedMode } = useAppMode();

  const {
    currentPage = 1,
    darkTheme = false,
    disabled = false,
    downloadable,
    fullScreenHandle,
    maxZoom,
    minZoom,
    onDownload,
    onFirstPageClick,
    onLastPageClick,
    onNextPageClick,
    onPrevPageClick,
    onZoomIn,
    onZoomOut,
    totalPages = 0,
    zoom = 1,
    zoomable = false,
  } = props;

  const isFullScreen = fullScreenHandle.active;
  const isFullScreenAllowed = canBeFullScreen();
  const isPreviousPageDisabled = disabled || currentPage === 1;
  const isLastPageDisabled = disabled || currentPage === totalPages;
  const isZoomInDisabled = disabled || (!!maxZoom && zoom >= maxZoom);
  const isZoomOutDisabled = disabled || (!!minZoom && zoom <= minZoom);
  const isZoomable = zoomable && !!onZoomIn && !!onZoomOut;

  const buttonBaseClasses = classNames({
    [fullScreenClasses.fullScreenIcon]: isFullScreen,
    [darkThemeClasses.primaryTextLight]: darkTheme,
  });

  const renderDownloadIconButton = (): JSX.Element => {
    const isDisabled = disabled || isEmbeddedMode;

    return (
      <ThemableIconButton
        aria-label="Download"
        className={classNames(buttonBaseClasses, {
          [darkThemeClasses.disabledTextLight]: darkTheme && isDisabled,
        })}
        disabled={isDisabled}
        onClick={onDownload}
        size="small"
        title="Download"
      >
        <GetApp />
      </ThemableIconButton>
    );
  };

  const renderDownloadButton = (): JSX.Element | undefined => {
    if (!downloadable) {
      return undefined;
    }

    if (!isEmbeddedMode) {
      return renderDownloadIconButton();
    }

    return (
      <Tooltip
        arrow
        placement="top"
        PopperProps={{ disablePortal: true }}
        title="Downloads are only enabled in the room link"
      >
        <a>{renderDownloadIconButton()}</a>
      </Tooltip>
    );
  };

  const renderZoomButtons = (): JSX.Element | undefined => {
    if (!isZoomable) {
      return undefined;
    }

    return (
      <>
        <ThemableIconButton
          aria-label="zoom out"
          className={classNames(buttonBaseClasses, {
            [darkThemeClasses.disabledTextLight]:
              darkTheme && isZoomOutDisabled,
          })}
          disabled={isZoomOutDisabled}
          onClick={onZoomOut}
          size="small"
          title="Zoom Out"
        >
          <ZoomOut />
        </ThemableIconButton>
        <ThemableIconButton
          aria-label="zoom in"
          className={classNames(buttonBaseClasses, {
            [darkThemeClasses.disabledTextLight]: darkTheme && isZoomInDisabled,
          })}
          disabled={isZoomInDisabled}
          onClick={onZoomIn}
          size="small"
          title="Zoom In"
        >
          <ZoomIn />
        </ThemableIconButton>
      </>
    );
  };

  const renderFullScreenButton = (): JSX.Element | undefined => {
    if (!isFullScreenAllowed) {
      return undefined;
    }

    const ariaLabel = isFullScreen ? 'fullscreen off' : 'fullscreen on';
    const title = isFullScreen ? 'Exit fullscreen' : 'Fullscreen';
    const Icon = isFullScreen ? FullscreenExit : Fullscreen;

    const clickHandler = isFullScreen
      ? fullScreenHandle.exit
      : fullScreenHandle.enter;

    return (
      <ThemableIconButton
        aria-label={ariaLabel}
        className={classNames(buttonBaseClasses, {
          [darkThemeClasses.disabledTextLight]: darkTheme && disabled,
        })}
        disabled={disabled}
        onClick={clickHandler}
        size="small"
        sx={{ ml: isZoomable ? 4 : 0 }}
        title={title}
      >
        <Icon />
      </ThemableIconButton>
    );
  };

  // adjusting with -34px (icon button width) if there are download or fullscreen
  // buttons, to make the page counter to be always at the center
  const marginLeft = downloadable ? -4.25 : 0;
  let marginRight = isFullScreenAllowed ? -4.25 : 0;

  if (isZoomable) {
    // adjusting the width of both zoom buttons and the margin the fullscreen
    // button adds when there are zoom buttons
    marginRight -= isFullScreenAllowed ? 12.5 : 8.5;
  }

  return (
    <Box
      alignItems="center"
      display="flex"
      margin="auto"
      px={2}
      sx={(theme) => ({
        maxWidth: '100%',
        width: theme.spacing(92.5),
      })}
    >
      {renderDownloadButton()}
      <Box
        alignItems="center"
        display="flex"
        flex="1"
        justifyContent="center"
        // adjustments to make the page counter always appear on the center
        sx={(theme) => ({
          ml: theme.spacing(marginLeft),
          mr: theme.spacing(marginRight),
        })}
      >
        <ThemableIconButton
          aria-label="first"
          className={classNames(buttonBaseClasses, {
            [darkThemeClasses.disabledTextLight]:
              darkTheme && isPreviousPageDisabled,
          })}
          disabled={isPreviousPageDisabled}
          onClick={onFirstPageClick}
          size="small"
          title="First"
        >
          <FirstPage />
        </ThemableIconButton>
        <ThemableIconButton
          aria-label="previous"
          className={classNames(buttonBaseClasses, {
            [darkThemeClasses.disabledTextLight]:
              darkTheme && isPreviousPageDisabled,
          })}
          disabled={isPreviousPageDisabled}
          onClick={onPrevPageClick}
          size="small"
          title="Previous"
        >
          <NavigateBefore />
        </ThemableIconButton>
        <Typography
          className={classNames({
            [darkThemeClasses.secondaryTextLight]: darkTheme || isFullScreen,
          })}
          color="textSecondary"
          variant="body2"
        >
          <Box
            className={classNames({
              [darkThemeClasses.primaryTextLight]: darkTheme || isFullScreen,
            })}
            component="span"
            sx={{ color: 'text.primary' }}
          >
            {currentPage}
          </Box>
          {`/${totalPages}`}
        </Typography>
        <ThemableIconButton
          aria-label="next"
          className={classNames(buttonBaseClasses, {
            [darkThemeClasses.disabledTextLight]:
              darkTheme && isLastPageDisabled,
          })}
          disabled={isLastPageDisabled}
          onClick={onNextPageClick}
          size="small"
          title="Next"
        >
          <NavigateNext />
        </ThemableIconButton>
        <ThemableIconButton
          aria-label="last"
          className={classNames(buttonBaseClasses, {
            [darkThemeClasses.disabledTextLight]:
              darkTheme && isLastPageDisabled,
          })}
          disabled={isLastPageDisabled}
          onClick={onLastPageClick}
          size="small"
          title="Last"
        >
          <LastPage />
        </ThemableIconButton>
      </Box>
      {renderZoomButtons()}
      {renderFullScreenButton()}
    </Box>
  );
};

export default DocumentActions;
